import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import KpiCard from "@/components/KpiCard";
import { fmtEur, fmtKwh, STATUS_LABELS, STATUS_COLORS } from "@/lib/format";
import {
  TrendingUp, FileCheck, Receipt, Sparkles, Flame,
  Users, MapPinned, FileText, Leaf, Zap, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";

function Ring({ pct, size = 112, stroke = 11, color = "#E0A82E", label }) {
  const cx = size / 2;
  const r = (size - stroke) / 2 - 1;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct || 0)) / 100;
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="#efeee9" strokeWidth={stroke} />
          <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${c * p} ${c}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums" style={{ fontSize: size * 0.21 }}>{Math.round(pct || 0)}%</div>
      </div>
      {label && <div className="mt-2 text-xs text-zinc-500 text-center leading-tight">{label}</div>}
    </div>
  );
}

const DONUT_COLORS = ["#7C7AE0", "#E0A82E", "#1C8FA8", "#22A85A", "#E2725B", "#E27FB0", "#94A3B8", "#5B8DEF"];

const QUICK = [
  { to: "/app/simulator", icon: MapPinned, t: "Simula un tetto", d: "Disegna e calcola", chip: "bg-amber-50 text-amber-600" },
  { to: "/app/customers", icon: Users, t: "Nuovo cliente", d: "Aggiungi al CRM", chip: "bg-sky-50 text-sky-600" },
  { to: "/app/quotes", icon: FileText, t: "Preventivi", d: "Gestisci e invia", chip: "bg-emerald-50 text-emerald-600" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)); }, []);

  const firstName = (user?.name || "").split(" ")[0] || "ciao";
  const monthly = s?.monthly || [];
  const statusBreakdown = s ? Object.entries(s.status_counts || {}).map(([k, v]) => ({
    label: STATUS_LABELS[k] || k, value: v, color: STATUS_COLORS[k] || "#94A3B8",
  })) : [];

  const funnel = s ? [
    { label: "Clienti", value: s.customers_total ?? 0, color: "#1C8FA8" },
    { label: "Simulazioni", value: s.simulations_total ?? 0, color: "#7C7AE0" },
    { label: "Preventivi", value: s.quotes_total ?? 0, color: "#E0A82E" },
    { label: "Accettati", value: s.quotes_accepted ?? 0, color: "#22A85A" },
  ] : [];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.value));

  const rings = s ? [
    { label: "Clienti → Simulazione", pct: s.customers_total ? (s.simulations_total / s.customers_total) * 100 : 0, color: "#1C8FA8" },
    { label: "Simulazione → Preventivo", pct: s.simulations_total ? (s.quotes_total / s.simulations_total) * 100 : 0, color: "#E0A82E" },
    { label: "Preventivo → Vinto", pct: s.quotes_total ? (s.quotes_accepted / s.quotes_total) * 100 : 0, color: "#22A85A" },
  ] : [];

  return (
    <div data-testid="page-dashboard">
      {/* ---- Greeting hero + quick actions ---- */}
      <div className="px-8 pt-8 pb-2">
        <div className="grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">Panoramica</div>
            <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Ciao {firstName} 👋</h1>
            <p className="text-zinc-500 mt-2 max-w-sm">Ecco come stanno andando le tue vendite. Pronto a chiudere il prossimo impianto?</p>
            <Link to="/app/simulator" className="mt-5 inline-flex items-center gap-2 px-5 py-3 bg-zinc-900 text-white font-semibold rounded-full text-sm hover:bg-zinc-800 active:scale-[0.99] transition-all" data-testid="cta-new-simulation">
              <Sparkles size={15} /> Nuova simulazione
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {QUICK.map((q) => {
              const Ic = q.icon;
              return (
                <Link key={q.t} to={q.to} className="group bg-white border border-zinc-200/70 rounded-3xl p-5 hover:border-zinc-300 hover:-translate-y-0.5 transition-all">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-8 ${q.chip}`}><Ic size={20} /></div>
                  <div className="font-semibold text-sm flex items-center justify-between">{q.t}<ArrowRight size={14} className="text-zinc-300 group-hover:text-zinc-900 transition-colors" /></div>
                  <div className="text-xs text-zinc-400 mt-0.5">{q.d}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-4">
        {/* ---- KPI vendite ---- */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testid="kpi-pipeline" label="Pipeline aperta" value={fmtEur(s?.pipeline_value ?? 0)} icon={TrendingUp} accent="yellow" />
          <KpiCard testid="kpi-won" label="Valore vinto" value={fmtEur(s?.accepted_value ?? 0)} icon={FileCheck} accent="green" />
          <KpiCard testid="kpi-avg" label="Valore medio preventivo" value={fmtEur(s?.avg_quote_value ?? 0)} icon={Receipt} accent="white" />
          <KpiCard testid="kpi-customers" label="Clienti totali" value={s?.customers_total ?? "—"} icon={Users} accent="cyan" />
        </div>

        {/* ---- Bar chart + Donut per fase ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-zinc-200/70 rounded-3xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Andamento</div>
            <div className="font-semibold text-lg mb-5">Preventivi creati · ultimi 6 mesi</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthly} margin={{ top: 6, right: 6, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 6" />
                <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <Tooltip cursor={{ fill: "transparent" }} contentStyle={{ background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Bar dataKey="quotes" name="Preventivi" radius={[6, 6, 6, 6]} fill="#E0A82E" maxBarSize={20} isAnimationActive={false} background={{ fill: "#f1f0ec", radius: 6 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-3xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Pipeline</div>
            <div className="font-semibold text-lg mb-4">Clienti per fase</div>
            <div className="flex items-center gap-4">
              <div className="relative w-[132px] h-[132px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusBreakdown.length ? statusBreakdown : [{ label: "—", value: 1, color: "#efeee9" }]} dataKey="value" nameKey="label" innerRadius={46} outerRadius={64} paddingAngle={2} stroke="none" isAnimationActive={false}>
                      {(statusBreakdown.length ? statusBreakdown : [{ color: "#efeee9" }]).map((e, i) => <Cell key={i} fill={statusBreakdown.length ? DONUT_COLORS[i % DONUT_COLORS.length] : "#efeee9"} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-semibold tabular-nums">{s?.customers_total ?? 0}</span>
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">clienti</span>
                </div>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                {statusBreakdown.length === 0 && <div className="text-zinc-400 text-sm">Nessun cliente</div>}
                {statusBreakdown.map((it, i) => (
                  <div key={it.label} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2 text-zinc-600 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />{it.label}</span>
                    <span className="font-mono text-zinc-900">{it.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- Funnel (barre orizzontali) + Conversione (anelli) + Impatto ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-zinc-200/70 rounded-3xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Funnel</div>
            <div className="font-semibold text-lg mb-5">Volume per fase</div>
            <div className="space-y-4">
              {funnel.map((f) => (
                <div key={f.label}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-zinc-600">{f.label}</span>
                    <span className="font-mono font-semibold text-zinc-900">{f.value}</span>
                  </div>
                  <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.max(4, (f.value / funnelMax) * 100)}%`, background: f.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-3xl p-6 flex flex-col">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Conversione</div>
            <div className="font-semibold text-lg mb-5">Tassi per fase</div>
            <div className="grid grid-cols-3 gap-2 mt-auto">
              {rings.map((r) => <Ring key={r.label} pct={r.pct} color={r.color} size={84} stroke={9} label={r.label} />)}
            </div>
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-3xl p-6 flex flex-col">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Impatto</div>
            <div className="font-semibold text-lg mb-5">Dai tuoi impianti</div>
            <div className="space-y-4 mt-auto">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><Zap size={18} /></div>
                <div>
                  <div className="font-mono text-lg font-semibold text-zinc-900">{fmtKwh(s?.total_annual_kwh ?? 0)}</div>
                  <div className="text-xs text-zinc-400">produzione / anno</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Leaf size={18} /></div>
                <div>
                  <div className="font-mono text-lg font-semibold text-zinc-900">{s?.co2_avoided_tonnes ?? 0} <span className="text-sm font-normal text-zinc-400">t/anno</span></div>
                  <div className="text-xs text-zinc-400">CO₂ evitata</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Ultimi preventivi ---- */}
        <div className="bg-white border border-zinc-200/70 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-lg flex items-center gap-2"><Flame size={18} className="text-amber-500" /> Preventivi recenti</div>
            <Link to="/app/quotes" className="text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors">Vedi tutti →</Link>
          </div>
          {(s?.recent_quotes || []).length === 0 && <div className="text-zinc-400 text-sm">Nessun preventivo. <Link to="/app/simulator" className="text-zinc-900 font-medium underline">Genera il primo</Link></div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(s?.recent_quotes || []).map((q) => (
              <Link to={`/app/quotes/${q.id}`} key={q.id} className="bg-zinc-50 border border-zinc-200/70 rounded-2xl p-4 hover:border-zinc-300 transition-colors block">
                <div className="font-mono text-xs text-zinc-400">{q.quote_number}</div>
                <div className="font-mono text-xl mt-1.5 text-zinc-900 font-semibold">{fmtEur(q.total)}</div>
                <div className="text-xs text-zinc-400 mt-1 capitalize">{q.status}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
